"use client";
 
import { User, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useUser } from "@/lib/context/user-context";
import { toast } from "sonner";
import Image from "next/image";
import { ForgotPasswordDialog } from "@/components/ui/forgot-password-dialog";
import { LanguageToggle } from "@/components/language-toggle";
import { useI18n } from "@/lib/i18n";

interface FullScreenLoginProps {
  defaultMode?: "login" | "signup";
}

export const FullScreenLogin = ({ defaultMode = "login" }: FullScreenLoginProps) => {
  const { t, lang } = useI18n() as any;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(defaultMode === "signup");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { loginWithEmail, user } = useUser();

  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };
 
  const validatePassword = (value: string) => {
    return value.length >= 6;
  };
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("=== FORM SUBMISSION START ===");
    console.log("Form submitted with:", { email, password, isSignUp });
    console.log("User context:", { user, loginWithEmail: typeof loginWithEmail });
    
    // Clear previous errors
    setEmailError("");
    setPasswordError("");
    
    let valid = true;
 
    if (!validateEmail(email)) {
      setEmailError(t('auth.errors.invalidEmail'));
      valid = false;
      console.log("Email validation failed");
    }
 
    if (!validatePassword(password)) {
      setPasswordError(t('auth.errors.shortPassword'));
      valid = false;
      console.log("Password validation failed");
    }
 
    if (!valid) {
      console.log("Form validation failed, stopping submission");
      return;
    }

    console.log("Form validation passed, starting authentication...");
    setIsLoading(true);

    try {
      console.log("About to call loginWithEmail function...");
      console.log("loginWithEmail function exists:", typeof loginWithEmail === 'function');
      
      if (isSignUp) {
        console.log("Attempting registration...");
        await loginWithEmail(email, password, true);
        console.log("Registration call completed");
        toast.success(lang === 'chinese' ? '账号创建成功！请前往邮箱完成验证。' : 'Account created successfully! Please check your email to verify your account.');
      } else {
        console.log("Attempting login...");
        await loginWithEmail(email, password, false);
        console.log("Login call completed successfully");
        toast.success(lang === 'chinese' ? '欢迎回来！登录成功。' : 'Welcome back! You have successfully signed in.');
      }
      
    } catch (error: any) {
      console.error('Authentication error caught:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    } finally {
      setIsLoading(false);
      console.log("=== FORM SUBMISSION END ===");
    }
  };
 
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-card rounded-2xl shadow-xl border overflow-hidden relative">
        {/* Language Toggle */}
        <div className="absolute right-4 top-4">
          <LanguageToggle />
        </div>
        {/* Header with Logo */}
        <div className="bg-card border-b px-8 py-6">
          <div className="flex items-center justify-center gap-3">
            <div className="flex items-center justify-center w-8 h-8">
              <Image 
                src="/logo1.png" 
                alt="Lingoflows Logo" 
                width={32} 
                height={32}
                className="w-8 h-8"
              />
            </div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              Lingoflows
            </h1>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row min-h-[500px]">
          {/* Left panel */}
          <div className="bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground p-8 lg:p-12 lg:w-2/5 flex items-center justify-center relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-20 h-20 border border-primary-foreground/20 rounded-full"></div>
              <div className="absolute bottom-20 right-10 w-16 h-16 border border-primary-foreground/20 rounded-full"></div>
              <div className="absolute top-1/2 right-5 w-12 h-12 border border-primary-foreground/20 rounded-full"></div>
            </div>
            
            <div className="text-center lg:text-left relative z-10">
              <h1 className="text-2xl lg:text-3xl font-semibold leading-tight mb-4">
                {isSignUp ? t('auth.headings.start') : t('auth.headings.welcome')}
              </h1>
              <p className="text-primary-foreground/80 text-sm lg:text-base leading-relaxed">
                {isSignUp ? t('auth.subheadings.leftSignup') : t('auth.subheadings.leftLogin')}
              </p>
            </div>
          </div>
 
          {/* Right panel with form */}
          <div className="p-8 lg:p-12 lg:w-3/5 flex flex-col justify-center bg-card">
            <div className="mb-8">
              <h2 className="text-2xl lg:text-3xl font-semibold mb-2 text-foreground">
                {isSignUp ? t('auth.headings.createAccount') : t('auth.headings.signIn')}
              </h2>
              <p className="text-muted-foreground">
                {isSignUp ? t('auth.subheadings.signup') : t('auth.subheadings.login')}
              </p>
            </div>
 
            <form
              className="space-y-5"
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-foreground">
                    {t('auth.form.email')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      placeholder={t('auth.form.emailPlaceholder')}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-background ${
                        emailError 
                          ? "border-destructive focus:ring-destructive" 
                          : "border-input hover:border-primary/50"
                      }`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-invalid={!!emailError}
                      aria-describedby="email-error"
                      disabled={isLoading}
                    />
                  </div>
                  {emailError && (
                    <p id="email-error" className="text-destructive text-sm flex items-center gap-1">
                      <span className="w-1 h-1 bg-destructive rounded-full"></span>
                      {emailError}
                    </p>
                  )}
                </div>
 
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-foreground">
                    {isSignUp ? t('auth.form.createPassword') : t('auth.form.password')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      placeholder={isSignUp ? t('auth.form.passwordPlaceholderSignup') : t('auth.form.passwordPlaceholderLogin')}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-background ${
                        passwordError 
                          ? "border-destructive focus:ring-destructive" 
                          : "border-input hover:border-primary/50"
                      }`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-invalid={!!passwordError}
                      aria-describedby="password-error"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <p id="password-error" className="text-destructive text-sm flex items-center gap-1">
                      <span className="w-1 h-1 bg-destructive rounded-full"></span>
                      {passwordError}
                    </p>
                  )}
                  
                  {/* Forgot Password Link - Only show in login mode */}
                  {!isSignUp && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs text-primary hover:text-primary/80 hover:underline transition-colors"
                        disabled={isLoading}
                      >
                        <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('auth.form.forgotPassword')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
 
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                  isLoading 
                    ? "bg-muted cursor-not-allowed text-muted-foreground" 
                    : "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin"></div>
                    {isSignUp ? t('auth.form.creatingAccount') : t('auth.form.signingIn')}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <User className="h-4 w-4" />
                    <span className={lang === 'chinese' ? 'font-normal' : ''}>{isSignUp ? t('auth.form.createAccount') : t('auth.form.signIn')}</span>
                  </div>
                )}
              </button>
 
              <div className="text-center">
                <p className="text-muted-foreground text-sm">
                  {isSignUp ? t('auth.form.haveAccount') : t('auth.form.dontHaveAccount')}{" "}
                  <button
                    type="button"
                    onClick={() => setIsSignUp(!isSignUp)}
                    className={`text-primary hover:text-primary/80 hover:underline transition-colors ${lang === 'chinese' ? 'font-normal' : 'font-semibold'}`}
                    disabled={isLoading}
                  >
                    {isSignUp ? t('auth.form.signInInstead') : t('auth.form.createAccount')}
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword} 
      />
    </div>
  );
}; 
